import { JobCard as Card } from '@seek/job-card';
import { useTranslations } from '@vocab/react';
import { useResponsiveValue } from 'braid-design-system';
import { memo, type ComponentProps, type MouseEvent } from 'react';

import { useSolHashByJobId } from 'src/context/solContext/solContext';
import useHubbleImpressionTracker from 'src/hooks/useHubbleImpressionTracker.ts';
import { featureExperimentRollout } from 'src/modules/featureExperimentRollout';
import { FeatureFlagsName } from 'src/modules/featureFlag';
import type { RecommendedGlobalJob } from 'src/queries/types';

import useSaveJob from '../../hooks/useSaveJob.ts';
import { useAppConfig } from '../App/useAppConfig';

import translations from './.vocab';

interface JobCardProps
  extends Pick<ComponentProps<typeof Card>, 'listPosition'> {
  job: RecommendedGlobalJob;
  setCurrentJob?: (job: RecommendedGlobalJob) => void;
  selectedJobId?: string;
}

export const useJobCardProps = ({
  id,
  selectedJobId,
}: {
  id: RecommendedGlobalJob['job']['id'];
  selectedJobId: JobCardProps['selectedJobId'];
}) => {
  const solMetadataHash = useSolHashByJobId(id);
  const url = [
    `job/${id}?ref=recom-standalone`,
    solMetadataHash ? `#sol=${solMetadataHash}` : '',
  ].join('');
  const selected = id === selectedJobId;

  return {
    url,
    selected,
  };
};

const useSalaryDisplayLabel = ({
  salary,
  currencyLabel,
}: {
  salary?: string;
  currencyLabel?: string | null;
}) => {
  const { t } = useTranslations(translations);

  if (!salary) {
    return;
  }

  return currencyLabel
    ? t('Salary display label', { salary, currencyLabel })
    : salary;
};

const JobCard = ({
  job,
  setCurrentJob,
  selectedJobId,
  listPosition,
}: JobCardProps) => {
  const { country, zoneFeatures } = useAppConfig();
  // TODO(DCS-9930): To be implemented in https://myseek.atlassian.net/browse/DCS-9930
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const showRemoteSearchFilter =
    featureExperimentRollout({
      featureToggle: FeatureFlagsName.REMOTE_SEARCH_FILTER,
      experimentName: 'REMOTE_SEARCH_FILTER',
      rolloutPercentage: 0,
    }) && Boolean(zoneFeatures.REMOTE_SEARCH_FILTER);

  const {
    solMetadata,
    isSaved,
    job: {
      id,
      advertiser,
      abstract,
      title,
      listedAt,
      salary,
      location,
      classifications,
      products,
    },
  } = job;

  const { toggleSavedJob } = useSaveJob({ id, isSaved, solMetadata });
  const isApplied = false; // todo check if this is needed to be got from job details

  const logoUrl = products?.branding?.logo?.url || undefined;

  const { url, selected } = useJobCardProps({
    id,
    selectedJobId,
  });

  const openJobDetailsInNewWindow = country === 'ph';

  const isAboveTablet = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true,
  });

  const handleClick =
    (selectedJob: RecommendedGlobalJob) => (e: MouseEvent) => {
      if (isAboveTablet && setCurrentJob) {
        e.preventDefault();
        setCurrentJob(selectedJob);

        // mark link visited
        const currentUrl = window.location.href;
        history.replaceState({}, '', url);
        history.replaceState({}, '', currentUrl);
      }
    };

  const salaryDisplayLabel = useSalaryDisplayLabel({
    salary: salary?.label,
    currencyLabel: salary?.currencyLabel,
  });

  const {
    zoneFeatures: { SHOW_JOBCARD_TEASER_IN_SPLITVIEW },
  } = useAppConfig();

  const { setRefs } = useHubbleImpressionTracker(id, solMetadata);

  return (
    <Card
      ref={setRefs}
      id={Number(id)}
      isAuthenticated={true}
      jobDetailPageLink={{ href: url, title }}
      jobDetailPageJobCardOriginLink={{ href: url, title }}
      jobDetailPageCardTitleOriginLink={{ href: url, title }}
      jobTitle={title}
      jobLinkOnClick={handleClick(job)}
      saveJob={{ isSaved, isApplied, returnUrl: '', toggleSavedJob }}
      teaser={abstract}
      srcLogo={logoUrl}
      salaryDisplayLabel={salaryDisplayLabel}
      aria-label={title}
      isSelected={selected}
      listingDate={listedAt?.label}
      onClick={handleClick(job)}
      shouldAddHoverEffect={!selected}
      companyLink={advertiser}
      classificationLinks={classifications.map((classification) => ({
        name: classification.label,
      }))}
      listPosition={listPosition}
      locationLinks={[{ name: location.label }]}
      featureFlags={{
        isShowJobCardTeaserInSplitView: SHOW_JOBCARD_TEASER_IN_SPLITVIEW,
        shouldOpenApplyInNewTab: openJobDetailsInNewWindow,
      }}
    />
  );
};

export default memo(JobCard);
